import { For, Show, createMemo, createSignal } from "solid-js";
import type { CatalogExercise } from "../api/types";
import CatalogExerciseSheet from "../components/CatalogExerciseSheet";
import ExerciseIcon from "../components/ExerciseIcon";
import PageHero from "../components/PageHero";
import Cell from "../components/tg/Cell";
import Section from "../components/tg/Section";
import { t } from "../i18n";
import { typeLabel } from "../lib/activity";
import { createDebounced } from "../lib/debounce";
import { useCatalog } from "../lib/data";
import {
  MUSCLE_GROUPS,
  muscleGroupChipClass,
  type MuscleGroupId,
} from "../lib/muscleGroups";

export default function ExercisesPage() {
  const [filterGroup, setFilterGroup] = createSignal<string | null>(null);
  const [search, setSearch] = createSignal("");
  const [sheetOpen, setSheetOpen] = createSignal(false);
  const [editing, setEditing] = createSignal<CatalogExercise | null>(null);

  // The whole catalog is loaded once (shared, cached); search filters it locally,
  // after a short pause in typing. No request per keystroke.
  const [catalog, { mutate }] = useCatalog();
  const query = createDebounced(() => search().trim().toLowerCase(), 250);
  const list = createMemo(() => {
    const q = query();
    const group = filterGroup();
    return (catalog() ?? []).filter(
      (ex) =>
        (!q || ex.name.toLowerCase().includes(q)) &&
        (!group || (ex.muscleGroups ?? []).includes(group)),
    );
  });

  const empty = createMemo(
    () =>
      !catalog.loading &&
      catalog() !== undefined &&
      list().length === 0 &&
      !sheetOpen(),
  );

  function openCreate() {
    setEditing(null);
    setSheetOpen(true);
  }

  function openEdit(ex: CatalogExercise) {
    setEditing(ex);
    setSheetOpen(true);
  }

  function upsert(item: CatalogExercise) {
    const cur = catalog() ?? [];
    mutate(
      cur.some((x) => x.id === item.id)
        ? cur.map((x) => (x.id === item.id ? item : x))
        : [item, ...cur],
    );
    setSheetOpen(false);
  }

  function dropped(id: string) {
    mutate((catalog() ?? []).filter((x) => x.id !== id));
    setSheetOpen(false);
  }

  function mgLabel(id: MuscleGroupId) {
    return t(`MG_${id.toUpperCase()}` as "MG_CHEST");
  }

  return (
    <div>
      <PageHero
        title={t("CATALOG")}
        aside={
          <button type="button" class="btn-ghost px-2" onClick={openCreate}>
            {t("CREATE")}
          </button>
        }
      />

      <Section>
        <div class="px-4 py-2">
          <input
            class="input"
            type="search"
            placeholder={t("SEARCH")}
            value={search()}
            onInput={(e) => setSearch(e.currentTarget.value)}
          />
        </div>
      </Section>

      <div class="-mx-1 overflow-x-auto px-1 pb-3">
        <div class="flex w-max gap-1.5">
          <button
            type="button"
            class={filterGroup() === null ? "tg-chip is-active" : "tg-chip"}
            onClick={() => setFilterGroup(null)}
          >
            {t("FILTER_ALL")}
          </button>
          <For each={[...MUSCLE_GROUPS]}>
            {(id) => (
              <button
                type="button"
                class={muscleGroupChipClass(id, filterGroup() === id)}
                onClick={() =>
                  setFilterGroup((cur) => (cur === id ? null : id))
                }
              >
                {mgLabel(id)}
              </button>
            )}
          </For>
        </div>
      </div>

      <CatalogExerciseSheet
        open={sheetOpen()}
        exercise={editing()}
        onClose={() => setSheetOpen(false)}
        onSaved={upsert}
        onDeleted={dropped}
      />

      <Show when={catalog.loading}>
        <p class="text-center text-[15px] text-ink-muted">{t("LOADING")}</p>
      </Show>

      <Show when={empty()}>
        <div class="empty-state">
          <p class="empty-state-title">{t("NO_CATALOG_EXERCISES")}</p>
        </div>
      </Show>

      <Show when={list().length > 0}>
        <Section>
          <For each={list()}>
            {(ex) => (
              <Cell
                leading={<ExerciseIcon icon={ex.icon} type={ex.exerciseType} />}
                title={ex.name}
                subtitle={[
                  typeLabel(ex.exerciseType),
                  ...(ex.muscleGroups ?? []).map((g) => mgLabel(g as MuscleGroupId)),
                ].join(" · ")}
                chevron
                onClick={() => openEdit(ex)}
              />
            )}
          </For>
        </Section>
      </Show>
    </div>
  );
}
